import { useMemo, useState } from 'react'
import type { AppConfig, Rule } from '@alerts/shared'
import { ChipGroup } from '../components/ChipGroup'
import { ProgressBar } from '../components/ProgressBar'

type Mode =
  | { kind: 'add-multi' }
  | { kind: 'edit-one'; market: string; existing: Rule | null }

export type WizardSubmit = (rules: { market: string; tickers: string[]; timeframes: string[]; pois: string[]; types: string[] }[]) => Promise<void>

type Props = {
  mode: Mode
  config: AppConfig
  existingRules?: Rule[]
  onCancel: () => void
  onSubmit: WizardSubmit
}

export function Wizard({ mode, config, existingRules = [], onCancel, onSubmit }: Props) {
  const isEditOne = mode.kind === 'edit-one'
  const totalSteps = isEditOne ? 3 : 4
  const [step, setStep] = useState(1)

  const [markets, setMarkets] = useState<string[]>(
    isEditOne ? [mode.market] : [],
  )
  const [tickers, setTickers] = useState<string[]>(mode.kind === 'edit-one' && mode.existing ? mode.existing.tickers : [])
  const [tfs, setTfs] = useState<string[]>(mode.kind === 'edit-one' && mode.existing ? mode.existing.timeframes : [])
  const [pois, setPois] = useState<string[]>(mode.kind === 'edit-one' && mode.existing ? mode.existing.pois : [])
  const [types, setTypes] = useState<string[]>(mode.kind === 'edit-one' && mode.existing ? mode.existing.types : [])

  const [submitting, setSubmitting] = useState(false)
  const [showError, setShowError] = useState(false)

  const existingByMarket = useMemo(() => {
    const m = new Map<string, Rule>()
    for (const r of existingRules) m.set(r.market, r)
    return m
  }, [existingRules])

  function handleMarketsChange(nextMarkets: string[]) {
    const prev = new Set(markets)
    const added = nextMarkets.filter((m) => !prev.has(m))
    if (added.length) {
      const tickerSet = new Set(tickers)
      const tfSet = new Set(tfs)
      const poiSet = new Set(pois)
      const typeSet = new Set(types)
      for (const m of added) {
        const r = existingByMarket.get(m)
        if (!r) continue
        for (const t of r.tickers) tickerSet.add(t)
        for (const t of r.timeframes) tfSet.add(t)
        for (const p of r.pois) poiSet.add(p)
        for (const t of r.types) typeSet.add(t)
      }
      if (tickerSet.size !== tickers.length) setTickers([...tickerSet])
      if (tfSet.size !== tfs.length) setTfs([...tfSet])
      if (poiSet.size !== pois.length) setPois([...poiSet])
      if (typeSet.size !== types.length) setTypes([...typeSet])
    }
    setMarkets(nextMarkets)
  }

  const availableTickers = markets.flatMap((m) => config.tickers[m] ?? [])
  const filteredSelected = tickers.filter((t) => availableTickers.includes(t))

  const onLastStep = step === totalSteps

  function validate(): string | null {
    if (!isEditOne && step === 1 && markets.length === 0) return 'Pick at least one market'
    if (step === (isEditOne ? 1 : 2) && filteredSelected.length === 0) return 'Pick at least one ticker'
    if (step === (isEditOne ? 2 : 3) && tfs.length === 0) return 'Pick at least one timeframe'
    if (onLastStep) {
      const missing: string[] = []
      if (pois.length === 0) missing.push('POI')
      if (types.length === 0) missing.push('Type')
      if (missing.length) return `Pick at least one ${missing.join(' and one ')}`
    }
    return null
  }
  const error = validate()

  function next() {
    if (error) {
      setShowError(true)
      return
    }
    setShowError(false)
    setStep(step + 1)
  }
  function back() {
    setShowError(false)
    if (step === 1) onCancel()
    else setStep(step - 1)
  }

  async function save() {
    if (error) {
      setShowError(true)
      return
    }
    setShowError(false)
    setSubmitting(true)
    try {
      const result = markets.map((m) => {
        const tickersForMarket = filteredSelected.filter((t) => (config.tickers[m] ?? []).includes(t))
        return { market: m, tickers: tickersForMarket, timeframes: tfs, pois, types }
      })
      await onSubmit(result.filter((r) => r.tickers.length > 0))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="app">
      <div style={{ marginBottom: 14, color: 'var(--text-muted)', fontSize: 13 }}>
        {isEditOne ? `Edit ${mode.market}` : 'New rules'} · Step {step} of {totalSteps}
      </div>
      <ProgressBar total={totalSteps} current={step} />

      {!isEditOne && step === 1 && (
        <ChipGroup label="Markets" options={config.markets} selected={markets} onChange={handleMarketsChange} />
      )}

      {step === (isEditOne ? 1 : 2) && (
        <>
          {markets.map((m) => (
            <ChipGroup
              key={m}
              label={m.toUpperCase()}
              options={config.tickers[m] ?? []}
              selected={filteredSelected.filter((t) => (config.tickers[m] ?? []).includes(t))}
              onChange={(next) => {
                const others = tickers.filter((t) => !(config.tickers[m] ?? []).includes(t))
                setTickers([...others, ...next])
              }}
              searchable
            />
          ))}
        </>
      )}

      {step === (isEditOne ? 2 : 3) && (
        <ChipGroup label="Timeframes" options={config.timeframes} selected={tfs} onChange={setTfs} />
      )}

      {onLastStep && (
        <>
          <ChipGroup label="POI" options={config.pois} selected={pois} onChange={setPois} />
          <ChipGroup label="Type" options={config.types} selected={types} onChange={setTypes} />
        </>
      )}

      {showError && error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <button type="button" className="btn-secondary" onClick={back}>←</button>
        {!onLastStep ? (
          <button type="button" className="btn-primary" onClick={next}>Next</button>
        ) : (
          <button type="button" className="btn-primary" onClick={save} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  )
}
